import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authAPI, usersAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { formatDateTimeForTimezone } from '../utils/datetime';
import './Settings.css';

export default function Settings() {
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const timezone = useAuthStore((state) => state.timezone);
  const setTimezone = useAuthStore((state) => state.setTimezone);

  const TIMEZONES = [
    { value: 'UTC', label: 'UTC' },
    { value: 'America/Guayaquil', label: 'UTC-5 (Ecuador / Guayaquil)' },
    { value: 'America/Bogota', label: 'UTC-5 (Colombia / Bogotá)' },
    { value: 'America/Lima', label: 'UTC-5 (Peru / Lima)' },
    { value: 'America/New_York', label: 'UTC-5/UTC-4 (US Eastern)' },
  ];

  const handleTimezoneChange = async (e) => {
    const newTz = e.target.value;
    setTimezone(newTz);

    // Persist timezone to backend user profile
    try {
      if (user?.id) {
        await usersAPI.update(user.id, { timezone: newTz });
      }
    } catch (error) {
      console.error('Failed to update timezone on server', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate form
    if (!formData.current_password) {
      toast.error('Please enter your current password');
      setLoading(false);
      return;
    }

    if (!formData.new_password) {
      toast.error('Please enter a new password');
      setLoading(false);
      return;
    }

    if (formData.new_password.length < 8) {
      toast.error('New password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.current_password === formData.new_password) {
      toast.error('New password must be different from current password');
      setLoading(false);
      return;
    }

    try {
      const response = await authAPI.changePassword({
        current_password: formData.current_password,
        new_password: formData.new_password,
        confirm_password: formData.confirm_password,
      });

      toast.success('Password changed successfully!');
      setFormData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-card">
        <h1>Settings</h1>
        
        <div className="user-info-section">
          <h2>User Information</h2>
          <div className="info-row">
            <label>Username:</label>
            <span>{user?.username}</span>
          </div>
          <div className="info-row">
            <label>Email:</label>
            <span>{user?.email}</span>
          </div>
          <div className="info-row">
            <label>User Type:</label>
            <span>{user?.user_type}</span>
          </div>
        </div>

        <div className="user-info-section">
          <h2>Time &amp; Locale</h2>
          <div className="info-row">
            <label>Timezone:</label>
            <select
              value={timezone}
              onChange={handleTimezoneChange}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>
          <div className="info-row">
            <label>Current Time Preview:</label>
            <span>
              {formatDateTimeForTimezone(new Date(), timezone)}
            </span>
          </div>
        </div>

        <hr className="divider" />

        <div className="change-password-section">
          <h2>Change Password</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Current Password</label>
              <input
                type="password"
                name="current_password"
                value={formData.current_password}
                onChange={handleChange}
                placeholder="Enter your current password"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                name="new_password"
                value={formData.new_password}
                onChange={handleChange}
                placeholder="Enter your new password (minimum 8 characters)"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                placeholder="Confirm your new password"
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
